import React from 'react';
import ReactAutocomplete from 'react-autocomplete';

import normalizeHTMLprops from 'ha/utils/normalizeHTMLprops';

export interface SuggestionArgs<T> {
  item: T;
  highlighted: boolean;
  value: string;
}

export type MenuItems = React.ReactNode[] | null;

export interface RenderMenuArgs {
  menuItems: MenuItems;
  menuValue: string;
}

type RenderReturnType = React.ReactNode | null;

export type RenderInputFn = (
  inputProps: React.HTMLProps<HTMLInputElement | HTMLTextAreaElement>,
  value: string,
) => RenderReturnType;

type RenderSuggestionFn<T> = (props: SuggestionArgs<T>) => RenderReturnType;

export type RenderMenuFn = (props: RenderMenuArgs) => RenderReturnType;

export interface RequiredProps<T> {
  // this is a render prop for rendering and input
  renderInput: RenderInputFn;
  // this is a render prop for rendering suggestions, they can be simple text, with imgs, etc
  renderSuggestion: RenderSuggestionFn<T>;
  // this is a render prop for rendering the menu, the place where items are rendered
  renderMenu: RenderMenuFn;
  // these are the items that will be rendered with renderSuggestion
  items: T[];
  // when the input changes it calls onChange
  onChange: (value: string) => void;
  // when selection occurs from picking a suggestion it calls onSelect
  onSelect: (value: string, item: T) => void;
  // function for getting menu item value according to the type you are handling
  getItemValue: (value: T) => string;
}

interface DefaultProps {
  // value for input passed down from outside
  value?: string;
  selectOnBlur?: boolean;
}

export type Props<T> = RequiredProps<T> & DefaultProps;
interface State {
  value: string;
}

function getInitialState<T>(props: Props<T>): State {
  return {
    value: props.value || '',
  };
}

class AutocompleteTextInput<T> extends React.Component<
  Props<T> & DefaultProps,
  State
> {
  static defaultProps: DefaultProps = { value: '', selectOnBlur: true };

  state = getInitialState(this.props);

  componentDidUpdate(prevProps: Props<T>, prevState: State) {
    if (
      this.props.value !== prevProps.value &&
      this.state.value === prevState.value
    ) {
      this.handleChange(this.props.value || '');
    }
  }

  handleChange = (v: string) => {
    this.setState({ value: v }, () => this.props.onChange(v));
  };

  handleSelect = (v: string, i: T) => {
    this.setState({ value: v }, () => this.props.onSelect(v, i));
  };

  render() {
    const { value } = this.state;
    const {
      getItemValue,
      renderInput,
      renderSuggestion,
      renderMenu,
      items,
      selectOnBlur,
    } = this.props;

    return (
      <ReactAutocomplete
        items={items}
        getItemValue={getItemValue}
        renderItem={(item: T, highlighted) =>
          renderSuggestion({ item, highlighted, value })
        }
        value={value}
        onChange={(_, v) => this.handleChange(v)}
        onSelect={(v, item) => this.handleSelect(v, item)}
        renderInput={p => renderInput(normalizeHTMLprops(p), value)}
        renderMenu={(menuItems, menuValue) =>
          renderMenu({ menuItems, menuValue })
        }
        selectOnBlur={selectOnBlur}
        wrapperStyle={{ display: 'block', position: 'relative' }} // default is inline-block but does not adjust full width for input
      />
    );
  }
}

export default AutocompleteTextInput;
